import { Modal, Stack, Text } from '@mantine/core'
import { isAdultJs } from 'macrotide-shared'
import { useState, type ReactNode } from 'react'

import { BirthDateField } from '../../components/BirthDateField'
import { FormActions } from '../../components/FormActions'
import { isIsoDate, todayIso } from '../../lib/days'
import { notifyError } from '../../lib/notify'
import { profileItem } from '../../sync/settings'
import { useSettings } from '../../sync/useSettings'
import { usePush } from '../../sync/useSync'
import { UnderAgeScreen } from './UnderAgeScreen'

/**
 * Sits in `Layout` so every signed-in app page gets it (setup asks for its own birth date on its own step instead,
 * `SetupPage`/`ProfileForm`'s `onUnderAge`). A profile without a birth date (an account from before one was
 * required) is asked once, with a non-dismissable modal reusing the profile's own field; once a birth date is on
 * the profile, under 18 (shared/ `isAdultJs`) replaces the app with `UnderAgeScreen`, same as setup.
 */
export function AgeGate(props: { children: ReactNode }) {
  const { profile } = useSettings()
  const push = usePush()
  const today = todayIso()
  const [birthDate, setBirthDate] = useState('')
  const [error, setError] = useState<string | undefined>()

  if (!profile) return <>{props.children}</>
  if (profile.birthDate !== null) {
    return isAdultJs(profile.birthDate, today) ? <>{props.children}</> : <UnderAgeScreen />
  }

  const submit = () => {
    if (!isIsoDate(birthDate) || birthDate >= today) {
      setError('Enter a birth date in the past.')
      return
    }
    push.mutate([profileItem({ ...profile, birthDate })], { onError: notifyError })
  }

  return (
    <>
      {props.children}
      <Modal opened title="Your birth date" onClose={() => {}} closeOnClickOutside={false} closeOnEscape={false} withCloseButton={false}>
        <Stack gap="md">
          <Text size="sm">We need this to make sure Macrotide is right for you.</Text>
          <BirthDateField value={birthDate} onChange={setBirthDate} today={today} error={error} required />
          <FormActions onSubmit={submit} loading={push.isPending} submitLabel="Save" />
        </Stack>
      </Modal>
    </>
  )
}
